import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, alpha, useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useVenues } from '../hooks/useVenues';
import { useVenueFilters } from '../hooks/useVenueFilters';
import { useCity } from '../../../contexts/CityContext';
import { VenueCard } from '../components/VenueCard';
import { CategoryPills } from '../components/CategoryPills';
import { SortDropdown } from '../components/SortDropdown';
import { ActiveFiltersChips } from '../components/ActiveFiltersChips';
import { FilterSidebar } from '../components/FilterSidebar';
import { FilterDrawer } from '../components/FilterDrawer';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ErrorDisplay } from '../../../components/ui/ErrorDisplay';
import { MainLayout } from '../../../components/layouts/MainLayout';

// Page Container with sidebar layout
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: 'calc(100vh - 64px)',
  backgroundColor: theme.palette.background.default,
}));

// Sidebar wrapper - hidden on mobile
const SidebarWrapper = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'block',
    flexShrink: 0,
  },
}));

// Main content area
const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '24px 16px',
  maxWidth: '100%',
  [theme.breakpoints.up('md')]: {
    padding: '32px 24px',
  },
  [theme.breakpoints.up('lg')]: {
    padding: '32px 32px',
  },
}));

// Header section with title and controls
const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: 24,
  [theme.breakpoints.up('md')]: {
    marginBottom: 32,
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.5rem',
  color: theme.palette.text.primary,
  marginBottom: 4,
  [theme.breakpoints.up('md')]: {
    fontSize: '2rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  [theme.breakpoints.up('md')]: {
    fontSize: '1rem',
  },
}));

// Top controls row
const ControlsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginBottom: 20,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const LeftControls = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const RightControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  [theme.breakpoints.up('md')]: {
    // Hide filter button on desktop (sidebar is visible)
    '& .mobile-filter-btn': {
      display: 'none',
    },
  },
}));

// Mobile filter button
const FilterButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: 10,
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.05),
    borderColor: theme.palette.success.main,
  },
}));

const FilterBadge = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: -4,
  right: -4,
  width: 18,
  height: 18,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  color: '#ffffff',
  fontSize: '0.6875rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Results info row
const ResultsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
  color: theme.palette.text.secondary,
}));

const ResultsCount = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

// Venue grid
const VenueGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 24,
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}));

// Pagination
const PaginationContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  marginTop: 32,
});

const PageButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontWeight: 500,
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  backgroundColor: isActive ? theme.palette.success.main : theme.palette.background.paper,
  color: isActive ? '#ffffff' : theme.palette.text.primary,
  boxShadow: isActive ? 'none' : `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  '&:hover:not(:disabled)': {
    backgroundColor: isActive ? theme.palette.success.dark : alpha(theme.palette.success.main, 0.1),
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const NavButton = styled('button')(({ theme }) => ({
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontWeight: 500,
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  '&:hover:not(:disabled)': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const VENUE_TYPE_DISPLAY: Record<string, string> = {
  GAMING: 'Gaming',
  SNOOKER: 'Snooker & Billiards',
  KIDS_PLAYAREA: 'Kids Play Area',
  gaming: 'Gaming',
  snooker: 'Snooker & Billiards',
  'kids-play': 'Kids Play Area',
};

export function VenuesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { selectedCity } = useCity();

  // Scroll to top when page loads or venue type filter changes via URL
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('type')]);

  const {
    filters,
    setFilters,
    clearFilters,
    activeFilters,
    removeFilter,
    hasActiveFilters,
    setPage,
    setType,
    setSortBy,
  } = useVenueFilters();

  const filtersWithCity = {
    ...filters,
    cityId: filters.cityId ?? selectedCity?.id,
  };

  const { data, isLoading, error } = useVenues(filtersWithCity);

  const handlePageChange = (page: number) => {
    setPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleVenueClick = (venue: { seoUrl?: string | null; slug: string }) => {
    navigate(venue.seoUrl || `/venues/${venue.slug}`);
  };

  const handleCategoryChange = (value: string | 'ALL') => {
    setType(value);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
  };

  const handleDrawerApply = () => {
    setDrawerOpen(false);
  };

  const activeFilterCount = activeFilters.filter((f) => !['sortBy'].includes(f.key)).length;

  const pageTitle = useMemo(() => {
    const typeLabel = filters.venueType ? VENUE_TYPE_DISPLAY[filters.venueType] : null;
    const cityName = selectedCity?.name ?? null;
    if (typeLabel && cityName) return `${typeLabel} Venues in ${cityName} | Lord of Venues`;
    if (cityName) return `Venues in ${cityName} | Lord of Venues`;
    if (typeLabel) return `${typeLabel} Venues | Lord of Venues`;
    return 'Find Venues | Lord of Venues';
  }, [filters.venueType, selectedCity]);

  const pageDescription = useMemo(() => {
    const typeLabel = filters.venueType ? VENUE_TYPE_DISPLAY[filters.venueType] : 'venues';
    const cityName = selectedCity?.name ?? 'India';
    return `Discover and book the best ${typeLabel.toLowerCase()} near you in ${cityName}. Transparent pricing, instant confirmation.`;
  }, [filters.venueType, selectedCity]);

  return (
    <MainLayout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://lordofvenues.com/venues" />
      </Helmet>
      <PageContainer>
        {/* Desktop Sidebar */}
        <SidebarWrapper>
          <FilterSidebar filters={filters} onFiltersChange={setFilters} onClear={clearFilters} />
        </SidebarWrapper>

        {/* Main Content */}
        <MainContent>
          <HeaderSection>
            <PageTitle>Find Your Perfect Venue</PageTitle>
            <PageSubtitle>
              Discover the best gaming arenas, snooker halls, and kids play areas near you
            </PageSubtitle>
          </HeaderSection>

          {/* Controls Row */}
          <ControlsRow>
            <LeftControls>
              <CategoryPills value={filters.venueType || 'ALL'} onChange={handleCategoryChange} />
            </LeftControls>
            <RightControls>
              <SortDropdown value={(filters.sortBy as string) || ''} onChange={handleSortChange} />
              {/* Mobile filter button */}
              <Box className="mobile-filter-btn" sx={{ position: 'relative' }}>
                <FilterButton onClick={() => setDrawerOpen(true)}>
                  <FilterListIcon />
                </FilterButton>
                {activeFilterCount > 0 && <FilterBadge>{activeFilterCount}</FilterBadge>}
              </Box>
            </RightControls>
          </ControlsRow>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <Box sx={{ mb: 2 }}>
              <ActiveFiltersChips
                filters={activeFilters}
                onRemove={removeFilter}
                onClearAll={clearFilters}
              />
            </Box>
          )}

          {/* Recently Viewed Section - Only show when no filters are active */}

          {/* Results Count */}
          {data && !isLoading && (
            <ResultsRow>
              <ResultsCount>
                Showing {data.data.length} of {data.total} venues
              </ResultsCount>
            </ResultsRow>
          )}

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <LoadingSpinner size="lg" />
            </Box>
          )}

          {/* Error State */}
          {error && <ErrorDisplay error={error} onAction={() => window.location.reload()} />}

          {/* Results */}
          {data && !isLoading && (
            <>
              {/* Venue Grid */}
              {data.data.length > 0 ? (
                <VenueGrid data-testid="venue-grid">
                  {data.data.map((venue) => (
                    <VenueCard
                      key={venue.id}
                      venue={venue}
                      onClick={() => handleVenueClick(venue)}
                      href={venue.seoUrl || `/venues/${venue.slug}`}
                    />
                  ))}
                </VenueGrid>
              ) : (
                <EmptyState variant="venues" actionLabel="Clear Filters" onAction={clearFilters} />
              )}

              {/* Pagination */}
              {data.totalPages > 1 && (
                <PaginationContainer>
                  <NavButton
                    onClick={() => handlePageChange(data.page - 1)}
                    disabled={data.page === 1}
                  >
                    Previous
                  </NavButton>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <PageButton
                          key={page}
                          isActive={data.page === page}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </PageButton>
                      );
                    })}
                    {data.totalPages > 5 && (
                      <>
                        <Typography sx={{ color: 'text.secondary', px: 1 }}>...</Typography>
                        <PageButton
                          isActive={data.page === data.totalPages}
                          onClick={() => handlePageChange(data.totalPages)}
                        >
                          {data.totalPages}
                        </PageButton>
                      </>
                    )}
                  </Box>

                  <NavButton
                    onClick={() => handlePageChange(data.page + 1)}
                    disabled={data.page === data.totalPages}
                  >
                    Next
                  </NavButton>
                </PaginationContainer>
              )}
            </>
          )}
        </MainContent>
      </PageContainer>

      {/* Mobile Filter Drawer */}
      {isMobile && (
        <FilterDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          filters={filters}
          onFiltersChange={setFilters}
          onApply={handleDrawerApply}
          onClear={clearFilters}
          resultCount={data?.total}
        />
      )}
    </MainLayout>
  );
}
